import jsQR from 'jsqr';
import { compressImageForAI } from './utils/image-compressor.js';
import { authManager } from './auth-manager.js';

export class AIScanner {
  constructor(containerId, onScanSuccess, onScanError) {
    this.containerId = containerId;
    this.onScanSuccess = onScanSuccess;
    this.onScanError = onScanError;
    this.stream = null;
    this.videoEl = null;
    this.canvasEl = null;
    this.ctx = null;
    this.animId = null;
    this.isScanning = false;
    this.zoom = 1.8;
    this.isTorchOn = false;
    this.currentMode = 'ai-label'; // 'ai-label' | '1d-barcode'
    this.lastDecodedCode = null;
    this.lastDecodedTime = 0;
    this._startToken = 0;
    this._stopRequested = false;
  }

  async start(mode = 'ai-label') {
    this.currentMode = mode;
    this._stopRequested = false;
    const currentToken = ++this._startToken;

    if (this.stream) {
      await this.stop();
    }
    if (this._stopRequested || this._startToken !== currentToken) {
      return;
    }

    const container = document.getElementById(this.containerId);
    if (!container) throw new Error(`Container #${this.containerId} não encontrado`);

    // Setup Video and Canvas Elements directly in container
    container.innerHTML = '';
    container.className = 'scanner-video-container';

    this.videoEl = document.createElement('video');
    this.videoEl.setAttribute('autoplay', 'true');
    this.videoEl.setAttribute('muted', 'true');
    this.videoEl.setAttribute('playsinline', 'true');
    this.videoEl.setAttribute('webkit-playsinline', 'true');
    this.videoEl.setAttribute('disablePictureInPicture', 'true');
    this.videoEl.setAttribute('disableRemotePlayback', 'true');
    this.videoEl.muted = true;
    this.videoEl.autoplay = true;
    this.videoEl.playsInline = true;
    this.videoEl.style.width = '100%';
    this.videoEl.style.height = '100%';
    this.videoEl.style.objectFit = 'cover';
    this.videoEl.style.transform = `scale(${this.zoom})`;
    container.appendChild(this.videoEl);

    // Overlay visual viewfinder
    const overlay = document.createElement('div');
    overlay.className = 'scan-overlay';
    overlay.innerHTML = `
      <div class="scan-frame product-mode">
        <div class="corner-bl"></div>
        <div class="corner-br"></div>
        <div class="scan-line"></div>
      </div>
    `;
    container.appendChild(overlay);

    // Offscreen Canvas for processing
    this.canvasEl = document.createElement('canvas');
    this.ctx = this.canvasEl.getContext('2d', { willReadFrequently: true });

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('Navegador sem suporte à câmera.');
    }

    // Request stream with progressive mobile fallbacks
    let stream = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } }
      });
    } catch (err) {
      console.warn('[AIScanner] Ideal camera constraint failed, trying environment facingMode:', err);
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: 'environment' }
        });
      } catch (e2) {
        console.warn('[AIScanner] Environment camera failed, trying generic video stream:', e2);
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: true
        });
      }
    }

    // If stop() was requested while getUserMedia was resolving, release hardware stream immediately!
    if (this._stopRequested || this._startToken !== currentToken) {
      if (stream) {
        try {
          stream.getTracks().forEach(track => track.stop());
        } catch (e) {}
      }
      return;
    }

    this.stream = stream;
    this.videoEl.srcObject = this.stream;

    const tryPlay = () => {
      if (this.videoEl && this.videoEl.paused) {
        this.videoEl.play().catch(e => console.warn('[AIScanner] Video play deferred:', e));
      }
    };

    this.videoEl.onloadedmetadata = tryPlay;
    this.videoEl.oncanplay = tryPlay;
    this.videoEl.onloadeddata = tryPlay;
    tryPlay();

    this.isScanning = true;

    if (mode === '1d-barcode') {
      this.startBarcodeScanLoop();
    }
  }

  startBarcodeScanLoop() {
    let barcodeDetector = null;
    if ('BarcodeDetector' in window) {
      try {
        barcodeDetector = new window.BarcodeDetector({
          formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'qr_code']
        });
      } catch (e) {}
    }

    const tick = async () => {
      if (!this.isScanning || this.currentMode !== '1d-barcode') return;

      if (this.videoEl && (this.videoEl.readyState >= 2 || (this.videoEl.videoWidth > 0 && this.videoEl.videoHeight > 0))) {
        const vW = this.videoEl.videoWidth;
        const vH = this.videoEl.videoHeight;

        if (vW > 0 && vH > 0) {
          const cropSize = Math.floor(Math.min(vW, vH) / this.zoom);
          const cropX = Math.floor((vW - cropSize) / 2);
          const cropY = Math.floor((vH - cropSize) / 2);

          const targetDim = 600;
          this.canvasEl.width = targetDim;
          this.canvasEl.height = targetDim;

          this.ctx.drawImage(this.videoEl, cropX, cropY, cropSize, cropSize, 0, 0, targetDim, targetDim);

          let decodedVal = null;
          let decodedFormat = 'EAN_13';

          if (barcodeDetector) {
            try {
              const barcodes = await barcodeDetector.detect(this.canvasEl);
              if (barcodes && barcodes.length > 0 && barcodes[0].rawValue) {
                decodedVal = barcodes[0].rawValue.trim();
                decodedFormat = barcodes[0].format || 'EAN_13';
              }
            } catch (e) {}
          }

          if (!decodedVal) {
            const imgData = this.ctx.getImageData(0, 0, targetDim, targetDim);
            const code = jsQR(imgData.data, targetDim, targetDim, { inversionAttempts: 'dontInvert' });
            if (code && code.data) {
              decodedVal = code.data.trim();
              decodedFormat = 'QR_CODE';
            }
          }

          if (decodedVal && decodedVal.length > 0) {
            const now = Date.now();
            if (decodedVal !== this.lastDecodedCode || (now - this.lastDecodedTime > 2500)) {
              this.lastDecodedCode = decodedVal;
              this.lastDecodedTime = now;

              if (navigator.vibrate) navigator.vibrate(100);

              const payload = {
                mode: '1d-barcode',
                ean: decodedVal,
                format: decodedFormat,
                descricao: '',
                descricao_bruta: '',
                preco: 0,
                source: 'barcode-camera'
              };

              this.onScanSuccess(payload);
            }
          }
        }
      }

      this.animId = requestAnimationFrame(tick);
    };

    this.animId = requestAnimationFrame(tick);
  }

  async stop() {
    this._stopRequested = true;
    this._startToken++;
    this.isScanning = false;
    if (this.animId) {
      cancelAnimationFrame(this.animId);
      this.animId = null;
    }
    if (this.stream) {
      try {
        this.stream.getTracks().forEach(track => {
          track.stop();
        });
      } catch (e) {}
      this.stream = null;
    }
    if (this.videoEl) {
      try {
        this.videoEl.pause();
        this.videoEl.srcObject = null;
      } catch (e) {}
    }
  }

  applyZoom(zoomLevel) {
    this.zoom = Math.max(1, Math.min(zoomLevel, 3.5));
    if (this.videoEl) {
      this.videoEl.style.transform = `scale(${this.zoom})`;
      this.videoEl.style.transformOrigin = 'center center';
      this.videoEl.style.transition = 'transform 180ms ease';
    }
    if (this.stream) {
      const track = this.stream.getVideoTracks()[0];
      if (track && track.applyConstraints) {
        track.applyConstraints({
          advanced: [{ zoom: this.zoom }]
        }).catch(() => {});
      }
    }
  }

  setZoom(zoomLevel) {
    this.applyZoom(zoomLevel);
  }

  hasTorch() {
    if (!this.stream) return false;
    const track = this.stream.getVideoTracks()[0];
    if (!track || !track.getCapabilities) return false;
    try {
      const caps = track.getCapabilities();
      return !!caps.torch;
    } catch (e) {
      return false;
    }
  }

  async toggleTorch() {
    if (!this.stream || !this.hasTorch()) return false;
    const track = this.stream.getVideoTracks()[0];
    try {
      this.isTorchOn = !this.isTorchOn;
      await track.applyConstraints({
        advanced: [{ torch: this.isTorchOn }]
      });
      return this.isTorchOn;
    } catch (err) {
      console.warn('[AIScanner] Torch toggle error:', err);
      return false;
    }
  }

  /**
   * Freezes the camera viewport on screen with the captured image overlay
   */
  freezePreview(dataUrl) {
    const container = document.getElementById(this.containerId);
    if (!container) return;

    let overlay = container.querySelector('#camera-frozen-overlay');
    if (!overlay) {
      overlay = document.createElement('img');
      overlay.id = 'camera-frozen-overlay';
      overlay.style.position = 'absolute';
      overlay.style.top = '0';
      overlay.style.left = '0';
      overlay.style.width = '100%';
      overlay.style.height = '100%';
      overlay.style.objectFit = 'cover';
      overlay.style.zIndex = '5';
      overlay.style.pointerEvents = 'none';
      overlay.style.borderRadius = 'inherit';
      overlay.style.transition = 'opacity 150ms ease';
      container.appendChild(overlay);
    }
    overlay.src = dataUrl;
    overlay.style.display = 'block';
    overlay.style.opacity = '1';

    if (this.videoEl) {
      try { this.videoEl.pause(); } catch (e) {}
    }
  }

  /**
   * Unfreezes the camera viewport and resumes live preview
   */
  unfreezePreview() {
    const container = document.getElementById(this.containerId);
    if (container) {
      const overlay = container.querySelector('#camera-frozen-overlay');
      if (overlay) {
        overlay.style.opacity = '0';
        setTimeout(() => {
          if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
        }, 150);
      }
    }
    if (this.videoEl && this.isScanning) {
      try { this.videoEl.play(); } catch (e) {}
    }
  }

  /**
   * Captures the current camera video frame directly into an optimized WebP blob and freezes it on screen
   */
  async captureFrame() {
    return this.captureCurrentFrame();
  }

  /**
   * Captures the current camera video frame directly into an optimized WebP blob and freezes it on screen
   */
  async captureCurrentFrame() {
    const container = document.getElementById(this.containerId);
    if (!container) throw new Error('Container do scanner não encontrado');

    const videoEl = container.querySelector('video');
    if (!videoEl || videoEl.videoWidth === 0) {
      throw new Error('Câmera ainda não inicializou o vídeo');
    }

    const maxDim = 720;
    let targetWidth = videoEl.videoWidth;
    let targetHeight = videoEl.videoHeight;

    if (targetWidth > maxDim || targetHeight > maxDim) {
      if (targetWidth > targetHeight) {
        targetHeight = Math.round((targetHeight * maxDim) / targetWidth);
        targetWidth = maxDim;
      } else {
        targetWidth = Math.round((targetWidth * maxDim) / targetHeight);
        targetHeight = maxDim;
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Contexto canvas indisponível');

    ctx.drawImage(videoEl, 0, 0, targetWidth, targetHeight);

    // Freeze the captured image immediately on screen
    try {
      const dataUrl = canvas.toDataURL('image/webp', 0.72);
      this.freezePreview(dataUrl);
    } catch (e) {
      console.warn('[AIScanner] Falha ao gerar preview congelado:', e);
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Falha ao capturar frame da câmera'));
      }, 'image/webp', 0.72);
    });
  }

  /**
   * Analyzes an image (File or Blob) using the backend Gemini Vision proxy
   * @param {File|Blob} imageFile 
   * @param {Function} onProgress Step callback
   * @param {AbortSignal} signal Optional abort signal
   * @returns {Promise<Object>} Extracted and semantically normalized data
   */
  async analyzeWithGemini(imageFile, onProgress, signal) {
    let compressedBlob = imageFile;

    // If file is not already compressed WebP, compress it
    if (!(imageFile.type === 'image/webp' && imageFile.size < 85 * 1024)) {
      if (onProgress) onProgress('Otimizando foto para upload instantâneo...');
      const result = await compressImageForAI(imageFile, 720, 0.72);
      compressedBlob = result.blob;
    }

    const sizeKB = Math.round(compressedBlob.size / 1024);
    if (onProgress) onProgress(`Consultando Gemini Flash (${sizeKB}KB)...`);

    // Prepare Multipart Request
    const formData = new FormData();
    formData.append('image', compressedBlob, 'label.webp');

    const token = authManager.getToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const apiUrl = '/api/scanner/analyze-label';
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers,
      body: formData,
      signal
    });

    if (!response.ok) {
      let errorMsg = 'Falha ao processar etiqueta na API';
      try {
        const errJson = await response.json();
        if (errJson.error) errorMsg = errJson.error;
      } catch (e) {}
      throw new Error(errorMsg);
    }

    const data = await response.json();
    return {
      mode: 'ai-label',
      ean: data.ean || '',
      descricao: data.descricao || '',
      descricao_bruta: data.descricao_bruta || '',
      preco: typeof data.preco === 'number' ? data.preco : 0.0,
      peso_ou_quantidade: data.peso_ou_quantidade || '',
      preco_por_kg_litro: data.preco_por_kg_litro || 0.0,
      model: data.model || 'gemini-2.5-flash',
      duration_ms: data.duration_ms || 0,
      estimated_cost_usd: data.estimated_cost_usd || 0,
      source: 'gemini-vision'
    };
  }
}
